<template>
  <div class="page-customer-structures" v-loading="loading">
    <portal to="layout-nav-title">客户/销售数据统计</portal>

    <CustomerStructuresMenu />

    <h2 class="title" style="margin-top: 0">{{ title }}</h2>

    <el-form inline>
      <el-form-item label="报告类型">
        <PeriodSelect :period.sync="form.period" :date.sync="form.date" />
      </el-form-item>
      <el-form-item>
        <DialogLoading
          :visible="exportLoading"
          description="正在生成导出文件，预计需要 1-3 分钟，请等待"
          :title="`导出处理中`"
        />
        <el-button type="primary" @click="handleExport">导出</el-button>
      </el-form-item>
    </el-form>

    <div class="box-data-card">
      <div
        class="data-card"
        v-for="group in cardGroup"
        :key="group.title"
        :style="`--primary-color: ${group.color}`"
      >
        <div class="box-left">
          <div class="box-left-title">{{ group.title }}</div>
          <div class="box-left-count">
            <app-locale-string :value="group.count" />
          </div>
          <div
            class="box-left-pop-rate"
            :class="{
              negative: parseFloat(group.growth) < 0,
              disabled: group.growth === '-',
            }"
          >
            同比 {{ group.growth }}
          </div>
        </div>
        <div v-if="group.teams && group.teams.length" class="box-right">
          <div class="group-row" v-for="(team, i) in group.teams" :key="i">
            <div class="group-row-name">
              {{ team.name }}
            </div>
            <div class="group-row-count">
              <app-locale-string :value="team.count" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="chart-container">
      <v-chart :options="chartOptions" autoresize />
    </div>

    <div class="mobile-table-data hidden-md-and-up">
      <div
        v-for="item in tableData"
        :key="item.type"
        class="mobile-table-data-item"
      >
        <div class="mobile-table-data-item-title">
          <div class="flex item-center">
            <span>{{ item.type }}</span>
            <span class="small-text"
              >（总数: <app-locale-string :value="item.total" />）</span
            >
          </div>
          <div class="small-text">较上月对比: {{ item.comparison }}</div>
        </div>
        <div class="mobile-table-data-level">
          <div v-for="level in data.levels" :key="level.name">
            {{ level.name }}:
            <app-locale-string :value="item[level.id]" />
          </div>
        </div>
      </div>
    </div>

    <el-table
      class="hidden-sm-and-down"
      :data="tableData"
      border
      style="width: 100%"
    >
      <el-table-column
        prop="type"
        label="客户等级"
        width="120"
        align="center"
        fixed="left"
      />
      <el-table-column
        v-for="level in data.levels"
        :key="level.name"
        :prop="String(level.id)"
        :label="level.name"
        align="center"
      >
        <template #default="{ row }">
          <app-locale-string :value="row[level.id]" />
        </template>
      </el-table-column>
      <el-table-column prop="total" label="总数" align="center">
        <template #default="{ row }">
          <app-locale-string :value="row.total" />
        </template>
      </el-table-column>
      <el-table-column prop="comparison" label="较上月对比" align="center" />
    </el-table>
  </div>
</template>

<script>
import { Vue, Component, Mixins, Watch } from 'vue-property-decorator';
import Utils from '@/components/utils.vue';
import moment from 'moment';
import { colors } from './colors';
import VChart from 'vue-echarts';
import _ from 'lodash';
import PeriodSelect from '@/components/period-select.vue';
import CustomerStructuresMenu from './menu.vue';
import DialogLoading from '@/components/dialog-loading.vue';
import { exportFile } from '@/utils/export';

@Component({
  components: {
    'v-chart': VChart,
    PeriodSelect,
    CustomerStructuresMenu,
    DialogLoading,
  },
})
export default class CustomerStructures extends Mixins(Vue, Utils) {
  exportLoading = false;
  form = {
    period: 'month',
    date: moment().startOf('month').format('YYYY-MM-DD'),
  };

  data = {};

  get date() {
    return this.form.date;
  }

  get title() {
    if (this.form.period === 'year') {
      return `${moment(this.date).format('YYYY年')}客户/销售数据统计`;
    }
    if (this.form.period === 'month') {
      return `${moment(this.date).format('YYYY年MM月')}客户/销售数据统计`;
    }
    const today = moment();
    const endWeek = moment(this.date).endOf('isoWeek');
    const endDate = moment.min(endWeek, today);
    const format = 'YYYY年MM月DD日';
    return `${moment(this.date)
      .startOf('isoWeek')
      .format(format)} ~ ${endDate.format(format)}客户/销售数据统计`;
  }

  get kindMap() {
    const {
      new_customer_count_details = [],
      followed_customer_count_details = [],
      total_customer_count_details = [],
      ordered_customer_count_details = [],
      order_amount_details = [],
      last_new_customer_count_details = [],
      last_followed_customer_count_details = [],
      last_total_customer_count_details = [],
      last_ordered_customer_count_details = [],
      last_order_amount_details = [],
    } = this.data;

    return [
      {
        key: 'new_customer_count',
        title: '新增客户',
        color: colors.by_new_customer_count,
        details: new_customer_count_details,
        lastDetails: last_new_customer_count_details,
      },
      {
        key: 'followed_customer_count',
        title: '触达客户',
        color: colors.by_followed_customer_count,
        details: followed_customer_count_details,
        lastDetails: last_followed_customer_count_details,
      },
      {
        key: 'total_customer_count',
        title: '在联总客户',
        color: colors.by_total_customer_count,
        details: total_customer_count_details,
        lastDetails: last_total_customer_count_details,
      },
      {
        key: 'ordered_customer_count',
        title: '成交总客户',
        color: colors.by_ordered_customer_count,
        details: ordered_customer_count_details,
        lastDetails: last_ordered_customer_count_details,
      },
      {
        key: 'order_amount',
        title: '成交总额',
        color: colors.by_order_amount,
        details: order_amount_details,
        lastDetails: last_order_amount_details,
      },
    ];
  }

  get cardGroup() {
    const { group_keys } = this.data;

    const getGroupData = ({ title, color, details, lastDetails, key }) => {
      const count = _.sumBy(details, (detail) => Number(detail[key]) || 0);
      const lastCount = _.sumBy(
        lastDetails,
        (detail) => Number(detail[key]) || 0
      );

      const detailsTeamGroup = _.groupBy(details, 'group_key');

      return {
        key,
        title,
        color,
        count: Number(count) || 0,
        growth: this.getGrowth(count, lastCount),
        teams: (group_keys || []).map(team => {
          return {
            name: team,
            count: _.sumBy(_.get(detailsTeamGroup, team), (detail) => Number(detail[key]) || 0)
          }
        })
      };
    };
    return this.kindMap.map(getGroupData);
  }

  loading = false;
  async fetchData() {
    try {
      this.loading = true;
      const data = await this.$request.get('/reports/customer_structures', {
        ...this.form,
        date: this.date,
      });
      this.data = _.mapValues(data, (dataGroup, key) => {
        if (key.endsWith('_details')) {
          return dataGroup.map((item) => {
            return {
              ...item,
            };
          });
        }
        return dataGroup;
      });
      // this.data = data;
    } finally {
      this.loading = false;
    }
  }

  mounted() {
    this.fetchData();
  }

  getGrowth(count, lastCount) {
    const countNumber = Number(count) || 0;
    const lastCountNumber = Number(lastCount) || 0;
    return lastCountNumber
      ? (((countNumber - lastCountNumber) / lastCountNumber) * 100).toFixed(2) +
          '%'
      : '-';
  }

  validPeriods = ['week', 'month', 'year'];

  created() {
    const query = this.$route.query;
    Object.assign(
      this.form,
      _.pickBy(
        query,
        (value, key) => value && Object.keys(this.form).includes(key)
      )
    );
    if (!this.validPeriods.includes(this.form.period)) {
      this.form.period = 'month';
      this.form.date = moment().startOf('month').format('YYYY-MM-DD');
    }
    this.debounceFetchData = _.debounce(this.fetchData, 500);
  }

  @Watch('form.period')
  @Watch('form.date')
  async handleChangeForm() {
    this.$router.replace({ query: { ...this.$route.query, ...this.form } }).catch(() => {});
  }

  @Watch('$route.query')
  async handleChangeQuery() {
    this.debounceFetchData();
  }

  get chartOptions() {
    return {
      color: Object.values(colors),
      title: {
        text: '成交总额客户类型占比',
        left: 'center',
        top: '0',
        textStyle: {
          color: '#999',
          fontSize: 14,
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        right: '0',
        top: 'middle',
        itemWidth: 15,
        itemHeight: 15,
        itemGap: 15,
        formatter: (name) => {
          const data = this.chartData.find((item) => item.name === name);
          return `${name}: ${data.percent}% (${data.value.toLocaleString()})`;
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['0%', '60%'],
          center: ['30%', '50%'],
          data: this.chartData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          label: {
            show: false,
          },
        },
      ],
    };
  }

  get chartData() {
    const {
      levels = [],
      order_amount_details = [],
      order_amount = 0,
    } = this.data;

    const total = Number(order_amount) || 0;

    return levels.map((level) => {
      const value = _.sumBy(
        order_amount_details.filter(
          (detail) => detail.customer_level_id === level.id
        ),
        (detail) => Number(detail.order_amount) || 0
      );
      const percent = total ? Math.round((value / total) * 100) : 0;

      return {
        name: level.name,
        value: value,
        percent: percent,
      };
    });
  }

  get tableData() {
    const { levels = [] } = this.data;

    const getCountByLevel = (details, countField) => {
      const detailsData = details;

      return levels.reduce((acc, level) => {
        acc[level.id] = detailsData
          .filter((detail) => detail.customer_level_id === level.id)
          .reduce((sum, detail) => sum + Number(detail[countField] || 0), 0);
        return acc;
      }, {});
    };

    const getCount = (details, countField) => {
      const detailsData = details;
      return _.sumBy(detailsData, (detail) => Number(detail[countField]) || 0);
    };

    const createRow = ({ key, title, details, lastDetails }) => {
      const count = getCount(details, key);
      const lastCount = getCount(lastDetails, key);
      const byLevel = getCountByLevel(details, key);
      const row = {
        type: title,
        total: count,
        comparison: this.getGrowth(count, lastCount),
      };

      levels.forEach((level) => {
        row[level.id] = byLevel[level.id] || 0;
      });
      return row;
    };

    return this.kindMap.map(createRow);
  }

  async handleExport() {
    try {
      this.exportLoading = true;
      await exportFile('reports/customer_structures', {
        ...this.form,
        date: this.date,
        return_xlsx: true,
      });
    } finally {
      this.exportLoading = false;
    }
  }
}

export const route = [
  {
    path: '/reports/customer_structures',
    name: 'reports/customer_structures',
    component: CustomerStructures,
    meta: {
      can: [],
    },
  },
];
</script>

<style lang="scss" scoped>
.page-customer-structures {
  padding: 20px;

  .el-table {
    margin-bottom: 20px;
  }
}

.box-data-card {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media screen and (max-width: 1600px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.data-card {
  background: var(--primary-color);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 20px;

  .box-left {
    flex: 1;

    &-title {
      font-size: 14px;
      color: #333;
      margin-bottom: 12px;
    }

    &-count {
      font-size: 24px;
      font-weight: bold;
      color: #333;
      margin-bottom: 8px;

      &::before {
        content: "+";
        margin-right: 4px;
      }
    }

    &-pop-rate {
      font-size: 12px;
      display: flex;
      align-items: center;
      gap: 4px;

      &::before {
        content: "";
        display: inline-block;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
      }

      &:not([class*="negative"]):not(.disabled) {
        color: #f56c6c;

        &::before {
          border-bottom: 6px solid #f56c6c;
        }
      }

      &.negative {
        color: #67c23a;

        &::before {
          border-top: 6px solid #67c23a;
        }
      }
    }
  }

  .box-right {
    flex: 1;

    .group {
      margin-bottom: 12px;

      &.borderd {
        border: 1px solid #ccc;
        padding: 5px;
      }
    }

    .group-row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &:last-child {
        margin-bottom: 0;
      }

      &-name {
        font-size: 13px;
        color: #666;
        flex: 1;
      }

      &-count {
        font-size: 13px;
        color: #333;
        min-width: 60px;
        text-align: right;

        &::before {
          content: "+";
          margin-right: 2px;
          color: #666;
        }

        &:not([class*="positive"]) {
          &::before {
            display: none;
          }
        }
      }
    }
  }
}

.chart-container {
  margin: 20px auto;
  width: 400px;
  max-width: 100%;
  height: 300px;

  @media screen and (max-width: 768px) {
    width: 100%;
    height: 300px;
  }
}

.echarts {
  width: 100%;
  height: 100%;
}

.mobile-table-data {
  margin-top: 20px;
  border-radius: 8px;

  &-item {
    font-size: 13px;
    color: #666;
    padding: 12px;
    margin-bottom: 12px;
    background: #bbdefb;
    border-radius: 8px;

    &-title {
      font-size: 14px;
      color: #333;
      margin-bottom: 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .small-text {
        font-size: 12px;
        color: #666;
      }
    }
  }

  &-level {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px 12px;
    margin-top: 8px;
  }
}
</style>
